import { availableAxis, type AxisType } from '../../../../../graph/axis';
import type { ElementGraphRuntimeSettings, RenderRuntimeGraphSettings } from '../types';
import type { AssertEqual } from '../../../../../utils/types';
import {
    getGraphTypeOwnedDataTypeSettings,
    type GraphBaseSettings,
    type GraphBehaviorSettings,
} from './contracts';

type DirectElementGraphRuntimeSettingsKey = Extract<
    keyof GraphBehaviorSettings,
    keyof RenderRuntimeGraphSettings
>;
type ElementGraphRuntimeSettingsKey = keyof ElementGraphRuntimeSettings;
type DirectGraphBehaviorSettings = Pick<
    GraphBehaviorSettings,
    DirectElementGraphRuntimeSettingsKey
>;
type GraphElementRuntimeSourceSettings = GraphBehaviorSettings &
    Pick<GraphBaseSettings, 'graphType'>;

const ELEMENT_GRAPH_RUNTIME_SETTINGS_KEYS = [
    'preferredGraphType',
    'frequencyGraphScaleType',
    'dataType',
    'valueScale',
    'valueScaleEquation',
    'frequencyScale',
    'hiddenLines',
] as const satisfies readonly DirectElementGraphRuntimeSettingsKey[];

const elementGraphRuntimeSettingsCoversDirectSettings: AssertEqual<
    (typeof ELEMENT_GRAPH_RUNTIME_SETTINGS_KEYS)[number],
    DirectElementGraphRuntimeSettingsKey
> = true;
void elementGraphRuntimeSettingsCoversDirectSettings;

function copyDefinedGraphRuntimeSetting<TKey extends ElementGraphRuntimeSettingsKey>(
    target: ElementGraphRuntimeSettings,
    source: ElementGraphRuntimeSettings,
    key: TKey
): void {
    const value = source[key];

    if (value !== undefined) {
        target[key] = value;
    }
}

function pickDefinedElementGraphRuntimeSettings(
    settings: DirectGraphBehaviorSettings
): ElementGraphRuntimeSettings {
    const elementGraphSettings: ElementGraphRuntimeSettings = {};

    ELEMENT_GRAPH_RUNTIME_SETTINGS_KEYS.forEach((key) =>
        copyDefinedGraphRuntimeSetting(elementGraphSettings, settings, key)
    );

    return elementGraphSettings;
}

// enabledAxes is kept as a distinct field (not folded into hiddenLines here) so that
// mergeElementGraphRuntimeSettings can resolve axis visibility and unrelated
// legend-line visibility against the report-level settings independently, instead
// of one wholesale-replacing the other.
export function getElementGraphRuntimeSettings(
    settings: GraphElementRuntimeSourceSettings
): ElementGraphRuntimeSettings {
    const elementGraphSettings: ElementGraphRuntimeSettings = {
        ...pickDefinedElementGraphRuntimeSettings(settings),
        ...getGraphTypeOwnedDataTypeSettings(settings.graphType),
    };

    if (typeof settings.enabledAxes !== 'undefined') {
        elementGraphSettings.enabledAxes = settings.enabledAxes;
    }

    return elementGraphSettings;
}

function partitionHiddenLines(hiddenLines: readonly string[]): {
    axisLines: string[];
    otherLines: string[];
} {
    const axisLines: string[] = [];
    const otherLines: string[] = [];

    hiddenLines.forEach((line) => {
        (availableAxis.includes(line as AxisType) ? axisLines : otherLines).push(line);
    });

    return { axisLines, otherLines };
}

function deriveHiddenAxisLines(enabledAxes: readonly AxisType[]): string[] {
    const enabledAxesSet = new Set(enabledAxes);
    return availableAxis.filter((axis) => !enabledAxesSet.has(axis));
}

// hiddenLines is a single flat array mixing axis identifiers (x/y/z/vector, for
// legacy PDFGeneratorAPI parity) with unrelated legend-type identifiers. Axis
// visibility and other-line visibility are independently overridable per element,
// so each half falls back to the report-level value on its own instead of an
// element-level override of one discarding report-level state for the other.
function resolveMergedHiddenLines(
    runtimeSettings: RenderRuntimeGraphSettings,
    elementGraphSettings: ElementGraphRuntimeSettings
): string[] {
    const reportHiddenLines = partitionHiddenLines(runtimeSettings.hiddenLines);

    const otherLines =
        typeof elementGraphSettings.hiddenLines === 'undefined'
            ? reportHiddenLines.otherLines
            : partitionHiddenLines(elementGraphSettings.hiddenLines).otherLines;

    const axisLines =
        typeof elementGraphSettings.enabledAxes === 'undefined'
            ? reportHiddenLines.axisLines
            : deriveHiddenAxisLines(elementGraphSettings.enabledAxes);

    return [...otherLines, ...axisLines];
}

export function mergeElementGraphRuntimeSettings(
    runtimeSettings: RenderRuntimeGraphSettings,
    elementGraphSettings: ElementGraphRuntimeSettings = {}
): RenderRuntimeGraphSettings {
    const definedElementGraphSettings =
        pickDefinedElementGraphRuntimeSettings(elementGraphSettings);

    return {
        ...runtimeSettings,
        ...definedElementGraphSettings,
        hiddenLines: resolveMergedHiddenLines(runtimeSettings, elementGraphSettings),
    };
}
